import React, { useEffect, useState } from "react";
import { Box, Flex } from "theme-ui";
import { themevals } from "theme/themevals";
import {
  getPlanProfitability,
  getProviderPerformance,
  PlanProfitability,
  ProviderPerformance,
  MetricPeriodType,
} from "api/Dashboard";

/**
 * The two reports DASHBOARD_REDESIGN_SPEC section 3.1 promised and
 * Reports never had: plan profitability and provider performance.
 *
 * BOTH ARE PERIOD-SCOPED, unlike everything else currently on the page.
 * A reports page whose reader cannot choose a window is a dashboard
 * with worse placement.
 *
 * Plan profitability runs through the same `planTable` the dashboard
 * uses, so the two cannot report different money for the same plan
 * (spec 7.13).
 *
 * PROVIDER PERFORMANCE IS OPERATOR-ONLY and must stay that way (spec
 * 7.10): published to providers these become negotiating positions, and
 * the PO provider-drift defect (register item 26) means a low match
 * rate is OUR data problem rather than their behaviour.
 */

const money = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const PERIODS: Array<{ key: MetricPeriodType; label: string }> = [
  { key: "month", label: "Month" },
  { key: "quarter", label: "Quarter" },
  { key: "year", label: "Year" },
];

const cellSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: "12px",
  color: themevals.colors.base_800,
  padding: "8px 10px",
  textAlign: "left" as const,
  whiteSpace: "nowrap" as const,
};

const headSx = {
  ...cellSx,
  fontWeight: 600,
  color: themevals.colors.dkgrey,
  borderBottom: "1px solid " + themevals.colors.inputOutline,
};

const Card: React.FC<{ title: string; note?: string; children: React.ReactNode }> = ({
  title,
  note,
  children,
}) => (
  <Box
    sx={{
      backgroundColor: "#FFFFFF",
      borderRadius: "6px",
      border: "1px solid",
      borderColor: themevals.colors.inputOutline,
      boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
      marginBottom: "24px",
      padding: "20px 24px",
    }}
  >
    <Box
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "14px",
        fontWeight: 600,
        color: themevals.colors.black,
      }}
    >
      {title}
    </Box>
    {note && (
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "11px",
          color: themevals.colors.dkgrey,
          marginTop: "4px",
        }}
      >
        {note}
      </Box>
    )}
    <Box sx={{ marginTop: "14px", overflowX: "auto" }}>{children}</Box>
  </Box>
);

const ReportsTables: React.FC = () => {
  const [period, setPeriod] = useState<MetricPeriodType>("year");
  const [plans, setPlans] = useState<PlanProfitability | null>(null);
  const [providers, setProviders] = useState<ProviderPerformance | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [p, v] = await Promise.all([
          getPlanProfitability(period),
          getProviderPerformance(period),
        ]);
        if (!cancelled) {
          setPlans(p);
          setProviders(v);
          setFailed(false);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (failed) return null;

  return (
    <Box>
      <Flex
        sx={{
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          marginBottom: "12px",
        }}
      >
        {PERIODS.map((p) => {
          const active = p.key === period;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              style={{
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                fontWeight: active ? 600 : 500,
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid " + themevals.colors.inputOutline,
                backgroundColor: active ? themevals.colors.base_800 : "#FFFFFF",
                color: active ? "#FFFFFF" : themevals.colors.dkgrey,
                cursor: "pointer",
              }}
            >
              {p.label}
            </button>
          );
        })}
      </Flex>

      <Card
        title="Plan profitability"
        note={
          plans
            ? plans.period.label +
              " - billed " +
              money(plans.totals.billed) +
              ", cost " +
              money(plans.totals.cost) +
              ", margin " +
              (plans.totals.margin === null
                ? "n/a"
                : plans.totals.margin.toFixed(1) + "%")
            : undefined
        }
      >
        <Box as="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box as="thead">
            <Box as="tr">
              <Box as="th" sx={headSx}>Plan</Box>
              <Box as="th" sx={headSx}>Sites</Box>
              <Box as="th" sx={headSx}>Work done</Box>
              <Box as="th" sx={headSx}>Billed</Box>
              <Box as="th" sx={headSx}>Collected</Box>
              <Box as="th" sx={headSx}>Cost</Box>
              <Box as="th" sx={headSx}>Margin</Box>
            </Box>
          </Box>
          <Box as="tbody">
            {(plans?.plans ?? []).map((plan) => (
              <Box as="tr" key={plan.planId}>
                <Box as="td" sx={cellSx}>{plan.name}</Box>
                <Box as="td" sx={cellSx}>{plan.sites}</Box>
                <Box as="td" sx={cellSx}>{plan.workDone}</Box>
                <Box as="td" sx={cellSx}>{money(plan.billed)}</Box>
                <Box as="td" sx={cellSx}>{money(plan.collected)}</Box>
                <Box as="td" sx={cellSx}>{money(plan.cost)}</Box>
                <Box as="td" sx={cellSx}>
                  {/* n/a rather than 0% - a plan with no billing has no
                      margin, it did not make nothing. */}
                  {plan.margin === null ? "n/a" : plan.margin.toFixed(1) + "%"}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>

      <Card
        title="Provider performance"
        note="Internal only. A low match rate is usually our data, not the provider."
      >
        <Box as="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box as="thead">
            <Box as="tr">
              <Box as="th" sx={headSx}>Provider</Box>
              <Box as="th" sx={headSx}>Orders</Box>
              <Box as="th" sx={headSx}>Spend</Box>
              <Box as="th" sx={headSx}>Avg days to accept</Box>
              <Box as="th" sx={headSx}>Unaccepted</Box>
              <Box as="th" sx={headSx}>PO match</Box>
            </Box>
          </Box>
          <Box as="tbody">
            {(providers?.providers ?? []).map((row) => (
              <Box as="tr" key={row.providerId}>
                <Box as="td" sx={cellSx}>{row.name}</Box>
                <Box as="td" sx={cellSx}>{row.orders}</Box>
                <Box as="td" sx={cellSx}>{money(row.spend)}</Box>
                <Box as="td" sx={cellSx}>
                  {/* Null means nothing has been accepted yet, which is
                      not the same as accepting instantly. */}
                  {row.avgDaysToAccept === null
                    ? "-"
                    : row.avgDaysToAccept.toFixed(1)}
                </Box>
                <Box
                  as="td"
                  sx={{
                    ...cellSx,
                    color:
                      row.unaccepted > 0
                        ? themevals.colors.danger
                        : cellSx.color,
                  }}
                >
                  {row.unaccepted}
                </Box>
                <Box as="td" sx={cellSx}>
                  {row.matchRate === null ? "-" : row.matchRate.toFixed(0) + "%"}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default ReportsTables;
